import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { Project } from '@shared/types'
import { combinedDashboardLabel } from '@shared/gcc/combine'
import { useApi, useGenerationContext } from '../index'
import { gccExecutiveVars } from './gccExecutiveStyle'
import { gccTnFailure } from './gccTnStatus'

export default function GccTnDashboardPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const api = useApi()
  const navigate = useNavigate()
  const { activeJobs, synced } = useGenerationContext()

  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!projectId) return
    let ignore = false
    setLoading(true)
    setError(null)
    void api.getProject(projectId).then(proj => {
      if (ignore) return
      setProject(proj)
      setLoading(false)
    }).catch(err => {
      if (ignore) return
      setError(err instanceof Error ? err.message : 'Failed to load project.')
      setLoading(false)
    })
    return () => { ignore = true }
  }, [api, projectId])

  const gccProjectId = project?.sourceGccProjectId
  const hasDashboard = !!project?.data
  const combined = project?.gccCombined
  // Same reason the GCC design's TN card shows, so a Failed card never opens to "still generating" (WI 2497)
  const failure = project ? gccTnFailure(project, activeJobs, synced) : null

  return (
    // The toolbar takes the dark palette of the TN dashboard hero below it, from the
    // same branding gcc-executive-layer uses, so the two read as one header (WI 2487).
    <div className="gcc-exec flex flex-col h-full" style={gccExecutiveVars(project?.branding)}>
      {/* Thin toolbar */}
      <div className="gx-toolbar gx-toolbar-tn flex-shrink-0">
        <button
          onClick={() => gccProjectId ? navigate(`/gcc/dashboard/${gccProjectId}`) : navigate(-1)}
          className="gx-crumb flex items-center gap-1.5"
        >
          ← Back to GCC
        </button>
        {project && !combined && (
          <>
            <span className="gx-sep">/</span>
            <span className="font-medium truncate max-w-xs">{project.name}</span>
          </>
        )}
        {combined && (
          <>
            <span className="gx-sep">/</span>
            <span className="gx-pill-dark">Combined</span>
            <span className="font-semibold">{combinedDashboardLabel(combined.offshoreScenario)}</span>
            <span className="gx-dim truncate" title="The scenario analysis's HQ + Markets roles are excluded so no one is counted twice">
              HQ + Markets roles from the Retained analysis · only GCC-eligible residual roles from {combined.offshoreScenario}
            </span>
          </>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Loading dashboard…</p>
            </div>
          </div>
        )}
        {!loading && (error || !hasDashboard) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center max-w-md px-6">
              {!error && failure && <p className="text-sm font-semibold text-red-700 mb-1">TN Analysis failed</p>}
              <p className="text-sm text-red-600 mb-4">
                {error ?? failure ?? 'Dashboard is still generating. Come back when the analysis completes.'}
              </p>
              <button
                onClick={() => gccProjectId ? navigate(`/gcc/dashboard/${gccProjectId}`) : navigate(-1)}
                className="text-xs text-purple-600 hover:underline"
              >
                ← Back to GCC
              </button>
            </div>
          </div>
        )}
        {!loading && !error && hasDashboard && (
          <iframe
            src={`/api/files/projects/${projectId}/dashboard-html`}
            className="w-full h-full border-0"
            title="TN Dashboard"
            sandbox="allow-scripts"
          />
        )}
      </div>
    </div>
  )
}
